/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/1/22, 3:54 PM
 *
 */

import React, { Fragment } from 'react';
import LetteredAvatar from 'react-avatar';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import { CaretDoubleRight, Copy, DotsThreeOutlineVertical, PhoneCall } from 'phosphor-react';
import { useAppDispatch } from 'stores';
import { Member } from 'types/MemberTypes';

import { resetPassword } from 'features/auth/api/requests/auth.request';
import { setMember } from 'features/members/index';

import { Badge } from '@sunyahealthnepal/react-ui-components';
import { alert } from 'components/Alert';

type OrgTableRowType = {
  data?: Member;
  key?: string | number;
  loading?: boolean;
};

export const MemberTableRow = ({ data, key, loading }: OrgTableRowType) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onMemberClick = () => {
    if (data) {
      dispatch(setMember(data));
      navigate(`/members/${data.role.slug}`);
    }
  };

  const copyHandler = (data: any) => {
    navigator.clipboard.writeText(data);
    toast.dismiss('reset-password-model');
    toast.success('Password Copied in Clipboard', { duration: 5000 });
  };
  const resetPasswordHandler = async (id: number) => {
    await alert({
      type: 'promise',
      promise: resetPassword(id),
      id: 'reset-password-model',
      msgs: {
        loading: 'Reseting Password',
        success: (datas: any) => {
          return (
            <>
              <div className="flex items-center gap-4 p-1">
                <p>New Password is {datas}</p>
                <button onClick={() => copyHandler(datas)} className="border">
                  <Copy size={24} color="#16a085" />
                </button>
              </div>
            </>
          );
        },
      },
      duration: 15000,
    });
  };
  return !loading ? (
    data ? (
      <tr key={key}>
        <td className="px-6 py-4">
          <div className="flex items-center">
            <div
              className="relative h-16 w-16 flex-shrink-0 cursor-pointer"
              onClick={onMemberClick}
            >
              <div
                className={`${
                  data.active ? 'bg-primary-500' : 'bg-red-700'
                } absolute right-0 z-10 h-4 w-4 rounded-full shadow-sm ring-2 ring-white`}
              />
              {data.image ? (
                <img
                  src={data.image}
                  className="relative h-[4.2rem] w-[4.2rem] overflow-hidden rounded-full object-contain"
                />
              ) : (
                <LetteredAvatar name={data.name} size="50" round={true} maxInitials={2} />
              )}
            </div>
            <div className="ml-4 flex flex-col gap-1">
              <div
                className="cursor-pointer text-xl font-medium  capitalize text-gray-900"
                onClick={onMemberClick}
              >
                {data.name}
              </div>
              <a
                href={`mailto: ${data.email}`}
                className="text-base font-light tracking-wide text-gray-500 hover:text-gray-800"
              >
                {data.email}
              </a>
            </div>
          </div>
        </td>
        <td className="  whitespace-nowrap px-6 py-4 font-mono text-base">
          <span className="font-Inter rounded-lg  bg-gray-100 px-2 py-1 font-bold tracking-wider text-gray-600">
            {data.member_code}
          </span>
        </td>

        <td className=" whitespace-nowrap px-6 py-4 text-base capitalize">
          <Badge
            type="error"
            condition={data && data.verified}
            trueStatement="Verified"
            falseStatement="Not Verified"
          />
        </td>

        <td className="whitespace-nowrap px-6 py-4 text-lg font-medium text-gray-500">
          <div className="flex items-center gap-2 hover:text-gray-800">
            <PhoneCall weight="duotone" size={18} />
            <a href={`tel:${data.phone}`}>{data.phone.slice(0, 14)}</a>
          </div>
        </td>
        <td className=" px-6 py-4">
          <div className="text-lg font-medium capitalize text-gray-700">{data.address}</div>
          <div className="text-lg font-medium text-gray-500">
            <a
              target="_blank"
              href={`https://maps.google.com/?q=${data.lat},${data.lng}`}
              rel="noreferrer"
            >
              <span className="flex cursor-pointer items-center hover:text-gray-800">
                Google Maps <CaretDoubleRight size={16} />
              </span>
            </a>
          </div>
        </td>

        <td className="px-4 py-4 ">
          <div className="flex items-center space-x-4 px-4">
            <Popover className="relative">
              <>
                <Popover.Button>
                  <DotsThreeOutlineVertical
                    size={24}
                    name="more-vertical"
                    className=" -ml-2 mt-1 cursor-pointer text-gray-400 hover:text-gray-800"
                  />
                </Popover.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 -translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-1"
                >
                  <Popover.Panel className="absolute right-2 z-50 mt-3 w-52 cursor-pointer space-y-2 rounded-sm bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="overflow-hidden" onClick={onMemberClick}>
                      <a className="group flex items-center bg-white transition duration-150 ease-in-out hover:bg-neutral-100 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-70">
                        <div className="flex items-center gap-2 py-3 px-4 text-xl text-neutral-700 group-hover:text-neutral-900 ">
                          <p className=" whitespace-nowrap font-medium  ">View Profile</p>
                        </div>
                      </a>
                    </div>
                    <div className="overflow-hidden" onClick={() => resetPasswordHandler(data.id)}>
                      <a className="group flex items-center bg-white transition duration-150 ease-in-out hover:bg-neutral-100 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-70">
                        <div className="flex items-center gap-2 py-3 px-4 text-xl text-neutral-700 group-hover:text-neutral-900 ">
                          <p className=" whitespace-nowrap font-medium  ">Reset Password </p>
                        </div>
                      </a>
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            </Popover>
          </div>
        </td>
      </tr>
    ) : null
  ) : null;
};
